import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, ButtonProps, Card, CardContent } from "@mui/material";
import { Avatar, IconButton } from "@outpave/ds";
import { HugeiconsIcon } from "@hugeicons/react";
import { MoreVerticalIcon } from "@hugeicons-pro/core-stroke-rounded";
import { useFeatureEntitlements } from "@frontegg/react";
import { Inline, Stack } from "components/layout/reusableLayouts";
import TableActionMenu from "components/common/TableActionMenu";
import LoadingWithoutModal from "components/common/LoadingWithoutModal";
import { Typography } from "@mui/material";
import { getDomain, logoLink } from "components/common/RenderMerchantDisplay";
import { useColorScheme } from "context/ColorSchemeContext";
import { IntegrationStatus } from "../../../enums/IntegrationStatus";
import {
  ConnectionStatus,
  IntegrationConnection,
  INTEGRATION_GROUP_LABEL_KEY,
  menuActions,
} from "utils/integrations/integrationConnections";

const ELLIPSIS = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  minWidth: 0,
} as const;

/** The status tones as text rather than as a pill, which is how the card reads. */
const STATUS_COLOR: Record<ConnectionStatus, string> = {
  [ConnectionStatus.CONNECTED]: "var(--color-status-success-text)",
  [ConnectionStatus.DISCONNECTED]: "var(--color-status-danger-text)",
  [ConnectionStatus.CONNECTING]: "var(--color-status-info-text)",
  [ConnectionStatus.DISCONNECTING]: "var(--color-status-info-text)",
  [ConnectionStatus.NOT_CONNECTED]: "var(--color-text-secondary)",
};

interface IntegrationCardProps {
  connection: IntegrationConnection;
  /** True while a connect or disconnect is in flight for this integration. */
  busy: boolean;
  /** How much of what this integration can map is mapped, or null if unknown. */
  mapping: { mapped: number; total: number } | null;
  onConnect: (connection: IntegrationConnection) => void;
  onReconnect: (connection: IntegrationConnection) => void;
  onManage: (connection: IntegrationConnection) => void;
  onDisconnect: (connection: IntegrationConnection) => void;
}

const IntegrationCard: React.FC<IntegrationCardProps> = ({
  connection,
  busy,
  mapping,
  onConnect,
  onReconnect,
  onManage,
  onDisconnect,
}) => {
  const { t } = useTranslation();
  const { isDark } = useColorScheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const { isEntitled } = useFeatureEntitlements(
    `integration_${connection.integrationType.toLowerCase()}`,
  );

  const actions = menuActions(connection, onDisconnect);
  const comingSoon = connection.status === IntegrationStatus.SUPPORTED;
  // No connection status means the merge treated it as catalogue only, so
  // there is nothing to connect to yet. INACTIVE lands here as well as
  // SUPPORTED, and used to draw a Connect the mutation could not honour.
  const connectable = Boolean(connection.connectionStatus);
  // Not Connected is what the Connect button beside it already says, and a
  // status pill on every browse card is noise. The group is the thing the flat
  // grid would otherwise lose.
  const showsStatus =
    Boolean(connection.connectionStatus) &&
    connection.connectionStatus !== ConnectionStatus.NOT_CONNECTED;

  if (connection.status === IntegrationStatus.ACTIVE && isEntitled === false)
    return null;

  const domain = getDomain(connection.url);
  // Ask for the variant that suits the card's own ground: the card follows the
  // scheme, so a logo drawn for a light background goes illegible in dark.
  const logoSrc = domain
    ? logoLink(domain, "icon", "lettermark", isDark ? "dark" : "light", 36)
    : undefined;

  const primary: {
    label: string;
    variant: ButtonProps["variant"];
    onClick?: () => void;
  } = (() => {
    switch (connection.connectionStatus) {
      case ConnectionStatus.CONNECTED:
        return {
          label: t("Manage"),
          variant: "default-outlined",
          onClick: () => onManage(connection),
        };
      case ConnectionStatus.DISCONNECTED:
        return {
          label: t("Reconnect"),
          variant: "primary-outlined",
          onClick: () => onReconnect(connection),
        };
      case ConnectionStatus.DISCONNECTING:
        return { label: t("Disconnecting"), variant: "default-outlined" };
      case ConnectionStatus.CONNECTING:
        return { label: t("Connecting"), variant: "primary-outlined" };
      default:
        return {
          label: t("Connect"),
          variant: "primary-outlined",
          onClick: connectable ? () => onConnect(connection) : undefined,
        };
    }
  })();

  const secondary = comingSoon ? (
    <Typography variant="body2" sx={{ color: "var(--color-text-secondary)" }}>
      {t("Coming Soon")}
    </Typography>
  ) : showsStatus && connection.connectionStatus ? (
    <Inline gap="var(--space-2)" wrap={false} sx={{ minWidth: 0 }}>
      <Typography
        variant="body2"
        sx={{ color: STATUS_COLOR[connection.connectionStatus] }}
      >
        {t(connection.connectionStatus)}
      </Typography>
      {connection.accountName && (
        <>
          <Typography
            variant="body2"
            aria-hidden
            sx={{ color: "var(--color-text-tertiary)" }}
          >
            ·
          </Typography>
          <Typography
            variant="body2"
            title={connection.accountName}
            sx={{ ...ELLIPSIS, color: "var(--color-text-secondary)" }}
          >
            {connection.accountName}
          </Typography>
        </>
      )}
    </Inline>
  ) : (
    <Typography variant="body2" sx={{ color: "var(--color-text-secondary)" }}>
      {t(INTEGRATION_GROUP_LABEL_KEY[connection.group] ?? connection.group)}
    </Typography>
  );

  const countLine =
    showsStatus && mapping ? (
      <Typography variant="body2" sx={{ color: "var(--color-text-tertiary)" }}>
        {t("integrationMappingCount", mapping)}
      </Typography>
    ) : null;

  return (
    <Card
      variant="outlined"
      sx={{
        // Scopes the busy overlay, which is otherwise a viewport-wide scrim.
        position: "relative",
        display: "flex",
        flexDirection: "column",
        // The grid stretches its cells, so every card in a row matches the
        // tallest and the buttons share a baseline. No pinned height.
        height: "100%",
        minWidth: 0,
      }}
    >
      <CardContent
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          gap: "var(--space-6)",
          minWidth: 0,
        }}
      >
        <Inline
          justify="between"
          align="start"
          gap="var(--space-4)"
          wrap={false}
        >
          <Inline
            gap="var(--space-5)"
            align="center"
            wrap={false}
            sx={{ minWidth: 0 }}
          >
            <Avatar
              src={logoSrc}
              name={connection.title}
              alt={connection.title}
              size="lg"
              variant="rounded"
            />
            <Stack gap="var(--space-1)" sx={{ minWidth: 0 }}>
              <Typography
                variant="h6"
                component="h3"
                title={connection.title}
                sx={ELLIPSIS}
              >
                {connection.title}
              </Typography>
              {secondary}
            </Stack>
          </Inline>

          {actions.length > 0 && (
            <IconButton
              size="small"
              variant="ghost"
              aria-label={t("Integration actions")}
              onClick={(event) => setAnchorEl(event.currentTarget)}
            >
              <HugeiconsIcon icon={MoreVerticalIcon} size={16} />
            </IconButton>
          )}
        </Inline>

        <Inline
          justify={countLine ? "between" : "end"}
          align="center"
          gap="var(--space-4)"
          wrap={false}
          sx={{ marginTop: "auto" }}
        >
          {countLine}
          <Button
            variant={primary.variant}
            size="small"
            disabled={busy || !primary.onClick}
            onClick={primary.onClick}
          >
            {primary.label}
          </Button>
        </Inline>
      </CardContent>

      <LoadingWithoutModal open={busy} contained curved smallLoading />

      <TableActionMenu
        id={`integration-actions-${connection.integrationType}`}
        anchorEl={anchorEl}
        menuItemDetails={actions.map((action) => ({
          label: t(action.labelKey),
          danger: action.danger,
          onClick: () => {
            action.onClick();
            setAnchorEl(null);
          },
        }))}
        handleCloseTableActions={() => setAnchorEl(null)}
      />
    </Card>
  );
};

export default IntegrationCard;
